import { useUserStore } from '@/store/user'
import { useSubscriptionStore } from '@/store/subscription'
import PlansCancelOffcanvas from './PlansCancelOffcanvas.vue'
import PlansCheckoutOffcanvas from './PlansCheckoutOffcanvas.vue'
import PlansClinicDataOffcanvas from './PlansClinicDataOffcanvas.vue'
import PlansUpdateOffcanvas from './PlansUpdateOffcanvas.vue'
import PlansPaymentMethodUpdateOffcanvas from './PlansPaymentMethodUpdateOffcanvas.vue'

const POST_SALES_WHATSAPP_NUMBER = '5516999930367';

// Clinics that paid the legacy plan in USD open a support ticket instead of using the WhatsApp line.
const POST_SALES_TICKET_URLS = {
  'pt-br': 'https://phelcom.com/pt-br/suporte/',
  es: 'https://phelcom.com/es/helpcenter/',
  en: 'https://phelcom.com/support/',
};

const findPriceByInterval = (prices, interval) =>
  (prices || []).find(price => price.recurring?.interval === interval);

const formatCapturesLimit = (limit, locale) => {
  if (limit === undefined || limit === null || limit === '')
    return ''

  const numericLimit = typeof limit === 'string'
    ? Number(limit.replace(/[^\d]/g, ''))
    : limit;

  return new Intl.NumberFormat(locale).format(numericLimit);
};

export default {
  name: 'new-plans',
  components: {
    PlansCancelOffcanvas,
    PlansCheckoutOffcanvas,
    PlansClinicDataOffcanvas,
    PlansUpdateOffcanvas,
    PlansPaymentMethodUpdateOffcanvas
  },
  data: () => ({
    loading: false,
    alert: {
      message: null,
      class: null
    },
    subscriptionName: '',
    displayName: '',
    subscriptionId: null,
    billingCycle: 'year',
    subscriptionDaysRemaining: null,
    usagePercentage: null,
    imageTotal: 50000,
    quotaAvailable: 50000,
    subscription: null,
    imageUsed: 0,
    planLimit: 10000,
    clinic: null,
    isDataReady: false,
  }),
  computed: {
    currentLocale() {
      return this.$i18n.locale
    },

    session() {
      return useUserStore().sessionInfo;
    },

    subscriptionStore() {
      return useSubscriptionStore();
    },

    // False for clinics still relying on the legacy (pre-migration) Stripe
    // account data - they have no real subscription on the new account to
    // cancel or update payment for, only the option to subscribe to a new plan.
    isMigrated() {
      return this.subscriptionStore.clinicUsage.migrated;
    },

    // Set by the backend (clinic-subscription.js) when this clinic's legacy
    // (pre-migration) subscription has already reached its periodEndDate.
    isExpired() {
      return this.subscriptionStore.clinicUsage.isExpired;
    },

    // Narrower than isExpired - excludes a voluntary cancellation or an
    // explicit decline, since those clinics already made their choice.
    needsResubscription() {
      return this.subscriptionStore.clinicUsage.needsResubscription;
    },

    hasLegacyCredit() {
      return this.subscriptionStore.clinicUsage.hasLegacyCredit;
    },

    legacyPlanCurrency() {
      return this.subscriptionStore.clinicUsage.legacyPlanCurrency;
    },

    // Covers a later renewal failing (past_due/unpaid) and the very first
    // charge failing outright (incomplete) - both need a new subscription to
    // fix, so both get the same signal.
    hasPaymentIssue() {
      return ['past_due', 'unpaid', 'incomplete'].includes(this.subscription?.status);
    },

    shouldShowIncompleteNfeWarning() {
      return this.isDataReady
        && this.isNfeDataIncomplete
        && this.subscriptionName !== 'Starter'
        && (this.isOwner || this.isAdmin)
        && !this.needsResubscription
        && !this.hasPaymentIssue;
    },

    formattedCapturesLimit() {
      return (limit) => formatCapturesLimit(limit, this.currentLocale)
    },

    filteredProducts() {
      const products = Object.values(this.allProductsData);

      return products.map(product => {
        const priceObj = product.prices.find(price =>
          price.recurring?.interval === this.billingCycle
        );

        if (priceObj) {
          return {
            ...product,
            price: priceObj,
            isPersonalized: product.metadata?.clinic_uuid === this.session.currentClinic.uuid
          };
        }

        return null
      }).filter(Boolean);
    },

    activePlanLimitText() {
      if (this.subscriptionName === 'Corporate' || this.planLimit === 'unlimited')
        return this.$i18n.t('plans.unlimited');

      return this.formattedCapturesLimit(this.planLimit);
    },
    // activePlanLimitText() {
    //   if (this.subscriptionName === 'Corporate' || this.planLimit === 'unlimited')
    //     return this.$i18n.t('plans.unlimited');
    //
    //   if (!this.subscriptionName || this.subscriptionName === 'Starter')
    //     return this.formattedCapturesLimit(10000);
    //
    //   return this.formattedCapturesLimit(Number(this.planLimit) + 10000);
    // },

    nextBillingDateFormatted() {
      if (!this.subscription?.current_period_end)
        return 'N/A'

      const endDate = new Date(this.subscription.current_period_end * 1000);
      return endDate.toLocaleDateString(this.currentLocale, {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    },

    activeDiscount() {
      const discount = this.subscription?.discounts?.[0];
      const coupon = discount?.source?.coupon ?? discount?.coupon;
      const price = this.subscription?.items?.data?.[0]?.price;

      if (!coupon || !price?.unit_amount)
        return null;

      let discountedAmount = price.unit_amount;
      if (coupon.percent_off)
        discountedAmount = Math.round(price.unit_amount * (1 - coupon.percent_off / 100));
      else if (coupon.amount_off)
        discountedAmount = Math.max(price.unit_amount - coupon.amount_off, 0);

      return {
        name: coupon.name,
        originalAmount: price.unit_amount,
        discountedAmount,
        currency: price.currency,
        // Stripe only fills `discount.end` for a `repeating` duration - `forever`/`once`
        // coupons never end while still applied, so there's no "valid until" to show.
        endsAt: coupon.duration === 'repeating' ? discount.end : null,
      };
    },

    couponOriginalPriceFormatted() {
      if (!this.activeDiscount)
        return null;

      return this.formatCurrency(this.activeDiscount.originalAmount, this.activeDiscount.currency);
    },

    couponValidUntilFormatted() {
      if (!this.activeDiscount?.endsAt)
        return null;

      const endDate = new Date(this.activeDiscount.endsAt * 1000);
      return endDate.toLocaleDateString(this.currentLocale, { month: 'short', year: 'numeric' });
    },

    activeSubscriptionPriceValue() {
      if (!this.subscription || !this.subscriptionName)
        return ""

      const subscriptionItem = this.subscription.items?.data?.[0]
      const finalPrice = subscriptionItem?.price
      const chargedAmount = this.activeDiscount?.discountedAmount ?? finalPrice?.unit_amount

      if (this.subscriptionDaysRemaining !== null && this.subscriptionDaysRemaining >= 0) {
        const formattedValue = this.formatCurrency(chargedAmount || 0, finalPrice?.currency)
        const interval = finalPrice?.recurring?.interval || 'month'
        const periodKey = interval === 'year' ? 'plans.yearly' : 'plans.monthly'

        return this.$i18n.t('plans.billingPhrase', {
          value: formattedValue,
          charged: this.$i18n.t('plans.chargedLabel'),
          period: this.$i18n.t(periodKey)
        });
      }

      if (finalPrice && chargedAmount) {
        const formattedValue = this.formatCurrency(chargedAmount, finalPrice.currency)
        const interval = finalPrice.recurring?.interval || 'month'
        const periodKey = interval === 'year' ? 'plans.yearly' : 'plans.monthly'

        return this.$i18n.t('plans.billingPhrase', {
          value: formattedValue,
          charged: this.$i18n.t('plans.chargedLabel'),
          period: this.$i18n.t(periodKey)
        })
      }

      return ""
    },

    activeCardInfo() {
      if (!this.subscription)
        return 'N/A';

      const card = this.subscription.default_payment_method?.card;

      if (card) {
        const brand = card.brand.charAt(0).toUpperCase() + card.brand.slice(1);
        const last4 = card.last4;

        return `${brand} ... ${last4}`;
      }

      return 'N/A';
    },

    cancellationStatusMessage() {
      if (this.subscriptionDaysRemaining !== null && this.subscriptionDaysRemaining >= 0) {
        const days = this.subscriptionDaysRemaining;

        if (days === 0)
          return this.$i18n.t('plans.cancellationStatusToday');

        const pluralKey = days === 1 ? 'plans.daySingular' : 'plans.dayPlural';
        const pluralWordTranslated = this.$i18n.t(pluralKey);
        return this.$i18n.t('plans.cancellationStatusDays', {
          days,
          plural: pluralWordTranslated
        });
      }

      return null;
    },

    usageTextFormatted() {
      const used = formatCapturesLimit(this.imageUsed, this.currentLocale);

      const limit = (typeof this.planLimit === 'string')
        ? this.planLimit
        : formatCapturesLimit(this.planLimit, this.currentLocale);

      return `${used} / ${limit} (${this.usagePercentage}%)`;
    },

    activeSubscriptionPriceFormatted() {
      const subscriptionItem = this.subscription?.items?.data?.[0]
      const finalPrice = subscriptionItem?.price

      if (!this.subscription || !this.subscriptionName || !finalPrice)
        return this.formatCurrency(0)

      return this.formatCurrency(finalPrice.unit_amount, finalPrice.currency)
    },

    // recommendedPlanName() {
    //   if (this.subscriptionName && this.subscriptionName !== 'Starter') {
    //     return null;
    //   }
    //
    //   const usage = this.imageUsed;
    //
    //   const suitablePlan = this.filteredProducts.find(item =>
    //     usage < (parseInt(item.metadata.captures_limit) * 0.7)
    //   );
    //
    //   if (suitablePlan) {
    //     return suitablePlan.name;
    //   }
    //   return 'Corporate';
    // },
    recommendedPlanName() {
      if (this.subscriptionName && this.subscriptionName !== 'Starter')
        return null;

      const usage = this.imageUsed;
      const suitablePlan = this.filteredProducts.find(item => {
        const totalLimitWithBonus = parseInt(item.metadata.captures_limit) + 10000;
        return usage < (totalLimitWithBonus * 0.7);
      });

      if (suitablePlan)
        return suitablePlan.name;

      return 'Corporate';
    },

    isOwner() {
      return this.session?.permissions.includes('owner');
    },

    isAdmin() {
      return this.session?.permissions.includes('admin');
    },

    allProductsData() {
      const productsMap = {};
      const products = this.subscriptionStore?.allProducts || [];

      // An add-on (e.g. AI Assistant) is not a plan: it never shows in the plans list
      products.filter(product => product.metadata?.type !== 'addon').forEach(product => {
        productsMap[product.name] = product;
      });

      return productsMap;
    },

    // % saved by paying yearly - read live from Stripe's prices instead of
    // hardcoded, so a price change alone doesn't need a new frontend release.
    yearlyDiscountRatio() {
      const products = Object.values(this.allProductsData);
      const product = products.find(p => findPriceByInterval(p.prices, 'month') && findPriceByInterval(p.prices, 'year'));
      if (!product)
        return null;

      const monthlyPrice = findPriceByInterval(product.prices, 'month').unit_amount;
      const yearlyPrice = findPriceByInterval(product.prices, 'year').unit_amount;

      return 1 - (yearlyPrice / (monthlyPrice * 12));
    },

    formattedYearlyDiscountPercent() {
      if (this.yearlyDiscountRatio == null)
        return '';

      return new Intl.NumberFormat(this.currentLocale, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      }).format(this.yearlyDiscountRatio * 100);
    },

    isLoading() {
      return this.subscriptionStore.loadingProducts || this.loading;
    },

    customClinicPlan() {
      return this.filteredProducts.find(p => p.isPersonalized);
    },

    publicPlans() {
      return this.filteredProducts.filter(p => !p.isPersonalized);
    },

    isNfeDataIncomplete() {
      if (!this.clinic)
        return true;

      const requiredFields = [
        'name',
        'document',
        'postalCode',
        'address',
        'addressNumber',
        'district',
        'city',
        'state',
        'country',
        'email',
        'telephone1'
      ];

      return requiredFields.some(field => {
        const value = this.clinic[field];

        if (!value || String(value).trim() === '') return true;

        if (field === 'telephone1' && String(value).trim().length < 3) return true;

        return false;
      });
    },

    isExactActivePlan() {
      return (item) => {
        const nameMatch = this.subscriptionName === item.name
        const activePrice = this.subscription?.items?.data?.[0]?.price
        const intervalMatch = activePrice?.recurring?.interval === this.billingCycle

        return nameMatch && intervalMatch
      };
    },
  },
  watch: {
    'session.currentClinic.country': {
      async handler(newCountry) {
        if (newCountry)
          await this.syncSubscriptionData();
      }
    }
  },
  async mounted() {
    if (!this.session?.currentClinic) {
      await this.$helpers.requestHandling(async () => {
        await this.$authService.refreshSession(this);

        if (this.session?.currentClinic?.country)
          await this.syncSubscriptionData();
      }, this);
    } else
      await this.syncSubscriptionData();

    this.isDataReady = true
  },
  methods: {
    goBack() {
      this.$router.go(-1)
    },

    resetSubscriptionData() {
      this.subscription = null;
      this.subscriptionId = null;
      this.subscriptionName = null;
      this.displayName = null;
      this.billingCycle = 'year';
      this.subscriptionDaysRemaining = null;
      this.planLimit = this.subscriptionStore.clinicUsage.planLimit;
    },

    getActiveProductName(subscriptions) {
      if (!subscriptions || subscriptions.length === 0)
        return null

      return subscriptions[0]?.product_name || null
    },

    updateBillingCycle(subscription) {
      const activePrice = subscription.items?.data?.[0]?.price;
      if (activePrice?.recurring?.interval)
        this.billingCycle = activePrice.recurring.interval;
    },

    updateSubscriptionDaysRemaining(subscription) {
      if (subscription.status === 'active' && subscription.cancel_at_period_end) {
        const endTimestamp = subscription.current_period_end * 1000;
        const endDate = new Date(endTimestamp);
        const today = new Date();
        const diffTime = endDate.getTime() - today.getTime();
        const MS_PER_DAY = 1000 * 60 * 60 * 24;

        this.subscriptionDaysRemaining = Math.ceil(diffTime / MS_PER_DAY);
      } else
        this.subscriptionDaysRemaining = null;
    },

    toCheckout(item) {
      this.$router.push({ name: 'checkout', query: { priceID: item.default_price, quantity: item.quantity } })
    },

    formatPrice(value, multiply) {
      let valueWithQuantity = value * multiply
      return (valueWithQuantity / 100).toFixed(2)
    },

    change(item, event) {
      item.quantity = event.target.value
      item.totalValue = this.formatPrice(item.price.unit_amount, event.target.value)
    },

    async openPlansCheckoutOffcanvas(item) {
      const priceID = item.price.id;

      if (!priceID) {
        this.$message.popup(this, 'priceNotFound', 'operationFail', 'error');
        return
      }

      const ok = await this.$refs.plansCheckoutOffcanvas.show({
        priceID,
        quantity: 1,
        clinicUUID: this.session.currentClinic.uuid,
        clinicEmail: this.session.currentClinic.email,
        clinicName: this.session.currentClinic.name
      });
      if (ok) {
        await this.refreshSession()
        this.fetchClinicData()
      }
    },

    async openPlansPaymentMethodUpdateOffcanvas() {
      const ok = await this.$refs.plansPaymentMethodUpdateOffcanvas.show({
        clinicUUID: this.session.currentClinic.uuid,
      });
      if (ok) {
        await this.refreshSession()
      }
    },

    // Legacy (not-migrated) clinics have no real subscription on the new
    // account to schedule a cancellation on - and still hold an unused credit
    // from their current legacy plan - so cancelling routes to post-sales
    // support instead of the normal cancellation flow.
    async handleCancelClick() {
      if (!this.isMigrated) {
        await this.$refs.plansMigrationNoticeOffcanvas.show({
          title: this.$i18n.t('plans.migrationCancelNotice.title'),
          text: this.$i18n.t('plans.migrationCancelNotice.text'),
          okButton: this.$i18n.t('plans.migrationCancelNotice.supportButton'),
          okButtonClass: 'btn-success',
          onConfirm: (resolve) => {
            this.goToPostSalesSupport();
            resolve(false);
          }
        });
        return;
      }

      await this.openPlansCancelOffcanvas();
    },

    // WhatsApp chat with post-sales support, opened with a message that already
    // identifies the clinic and says what the clinic needs (flow: cancel | change | resubscribe).
    postSalesWhatsappUrl(flow) {
      const message = [
        this.$i18n.t('plans.postSalesWhatsapp.greeting', { clinic: this.session.currentClinic.name }),
        this.$i18n.t(`plans.postSalesWhatsapp.${flow}`)
      ].join(' ');

      return `https://wa.me/${POST_SALES_WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
    },

    // Where a not-migrated clinic reaches post-sales, by the currency of its legacy plan:
    // USD -> support ticket page, other known currency -> WhatsApp, unknown -> help center
    // (the backend logs the unknown case).
    postSalesContactUrl(flow) {
      const isUsd = this.legacyPlanCurrency?.toLowerCase() === 'usd';
      const isCurrencyUnknown = !this.legacyPlanCurrency;

      if (isUsd)
        return POST_SALES_TICKET_URLS[this.currentLocale] || POST_SALES_TICKET_URLS.en;
      else if (isCurrencyUnknown)
        return this.helpcenterLink();
      else
        return this.postSalesWhatsappUrl(flow);
    },

    // Opens the post-sales contact - used while cancelling a legacy
    // plan during the payment system migration, since there's no way to
    // proceed with a normal cancellation.
    goToPostSalesSupport() {
      const link = document.createElement('a');
      link.href = this.postSalesContactUrl('cancel');
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    async openPlansCancelOffcanvas(item) {
      const locale = this.$i18n.locale === 'en' ? 'en-US' : 'pt-BR';
      const startDate = this.subscription?.current_period_start ? new Date(this.subscription.current_period_start * 1000).toLocaleDateString(locale) : '--/--/----';
      const endDate = this.subscription?.current_period_end ? new Date(this.subscription.current_period_end * 1000).toLocaleDateString(locale) : '--/--/----';

      const ok = await this.$refs.plansCancelOffcanvas.show({
        planName: this.displayName,
        planLimit: this.activePlanLimitText,
        startDate,
        endDate
      });
      if (ok) {
        await this.downgradeSubscription()
      }
    },

    async openPlansUpdateOffcanvas(item) {
      const currentPriceObj = this.subscription?.items?.data?.[0]?.price;
      const currentInterval = currentPriceObj?.recurring?.interval || 'month';
      const currentCurrency = currentPriceObj?.currency || 'usd';

      const currentPlan = {
        name: this.displayName,
        limit: this.activePlanLimitText,
        price: this.formatCurrency(currentPriceObj?.unit_amount, currentCurrency),
        interval: currentInterval
      };

      const desiredInterval = item.price.recurring?.interval || 'month';
      const desiredCurrency = item.price.currency || 'usd';

      const desiredPlan = {
        name: item.name,
        limit: this.formattedCapturesLimit(Number(item.metadata.captures_limit) + 10000),
        price: this.formatCurrency(item.price.unit_amount, desiredCurrency),
        interval: desiredInterval
      };

      const ok = await this.$refs.plansUpdateOffcanvas.show({
        current: currentPlan,
        desired: desiredPlan
      });

      if (ok) {
        await this.updateSubscription(item)
      }
    },

    async updateSubscription(item) {
      try {
        this.loading = true;
        const params = {
          subscriptionId: this.subscriptionId,
          newPriceID: item.price.id,
        };

        await this.$stripeService.updateSubscription(params);

        this.$message.popup(this, 'plans.subscriptionUpdated', 'success', 'success');
        await new Promise(resolve => setTimeout(resolve, 1000));
        await this.refreshSession();

      } catch (err) {
        console.error("Error updating subscription:", err);
        const errorMsg = err.response?.data?.message;

        switch (errorMsg) {
          case 'NOT_ALLOWED':
            this.$message.popup(this, 'plans.notAllowed', 'operationFail', 'error');
            break;

          case 'UNAUTHORIZED_PLAN':
            this.$message.popup(this, 'plans.unauthorizedPlan', 'operationFail', 'error');
            break;

          case 'INVALID_QUANTITY':
            this.$message.popup(this, 'plans.invalidQuantity', 'operationFail', 'error');
            break;

          case 'PRICE_NOT_FOUND':
            this.$message.popup(this, 'plans.priceNotFound', 'operationFail', 'error');
            break;

          default:
            this.$message.popup(this, 'unexpectedError', 'operationFail', 'error');
            break;
        }
      } finally {
        this.loading = false;
      }
    },

    async downgradeSubscription() {
      await this.$helpers.requestHandling(async () => {
        const params = {
          subscriptionId: this.subscriptionId,
          cancelAtPeriodEnd: true
        };

        await this.$stripeService.cancelSubscription(params);
        const offcanvasElement = document.querySelector('.offcanvas.show');
        if (offcanvasElement) {
          const bsOffcanvas = bootstrap.Offcanvas.getInstance(offcanvasElement);
          if (bsOffcanvas) {
            bsOffcanvas.hide();
          }
        }

        this.$message.popup(this, 'plans.downgradeScheduled', 'success', 'success');

        this.refreshSession()
      }, this, (err) => {
        const errorMsg = err.response?.data?.message;
        if (errorMsg === 'NOT_ALLOWED') return 'plans.notAllowed';
        if (errorMsg === 'SUBSCRIPTION_NOT_FOUND') return 'plans.subscriptionNotFound';
        return 'unexpectedError';
      });
    },

    async refreshSession() {
      await this.$helpers.requestHandling(async () => {
        await this.$authService.refreshSession(this);

        this.$nextTick(async () => {
          await this.syncSubscriptionData();
        });
      }, this);
    },

    calculateAndFormatCostPerImage(item) {
      const rawLimit = item.metadata?.captures_limit;
      const priceObj = item.price;

      if (!rawLimit || !priceObj || !priceObj.unit_amount)
        return this.$i18n.t('plans.notApplicableValue')

      const totalAmount = priceObj.unit_amount / 100;
      const monthlyAmount = priceObj.recurring?.interval === 'year'
        ? totalAmount / 12
        : totalAmount;
      const limit = parseInt(rawLimit) + 10000;

      if (limit === 0)
        return this.$i18n.t('plans.notApplicableValue')

      const costPerImage = monthlyAmount / limit

      return new Intl.NumberFormat(this.$i18n.locale, {
        style: 'currency',
        currency: priceObj.currency.toUpperCase(),
        minimumFractionDigits: 4,
        maximumFractionDigits: 4
      }).format(costPerImage);
    },

    formatPriceByMonth(item) {
      const selectedPrice = item.price

      if (!selectedPrice?.unit_amount)
        return this.formatCurrency(0)

      return this.formatCurrency(selectedPrice.unit_amount, selectedPrice.currency)
    },

    formatCurrency(valueInCents, currencyCode) {
      if (valueInCents === null || valueInCents === undefined)
        return ''

      const value = valueInCents / 100;
      const currentLocale = this.$i18n.locale;
      const clinicCountry = this.session?.currentClinic?.country?.toUpperCase();
      const fallbackByCountry = clinicCountry === 'BR' ? 'BRL' : 'USD';

      const finalCurrency = (currencyCode || fallbackByCountry || 'USD').toUpperCase();

      try {
        return new Intl.NumberFormat(currentLocale, {
          style: 'currency',
          currency: finalCurrency,
        }).format(value);
      } catch (err) {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(value);
      }
    },

    async updateCLinicPlans(_params) {
      const params = {
        clinicUUID: _params.clinicUUID,
        newLimit: _params.newLimit
      }

      await this.$clinicService.updateClinicPlan(params);
    },

    getPercentageValue() {
      this.usagePercentage = this.$dashboardService.getPercentageValue(
        this.imageUsed,
        this.planLimit
      );
    },

    getPercentageClass() {
      return this.$dashboardService.getPercentageClass(this.usagePercentage)
    },

    helpcenterLink() {
      return this.currentLocale == 'pt-br'
        ? 'https://phelcom.com/pt-br/helpcenter/'
        : 'https://phelcom.com/helpcenter/';
    },

    helpcenterUrl() {
      const link = document.createElement('a');
      link.href = this.helpcenterLink();
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    leadForms() {
      const url = this.currentLocale == 'pt-br'
        ? 'https://phelcom.com/pt-br/produtos/eyercloud/planos/corporate/'
        : 'https://phelcom.com/products/eyercloud/plans/corporate/';

      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    disclaimerUrl() {
      const url = this.currentLocale == 'pt-br'
        ? 'https://phelcom.com/pt-br/produtos/eyercloud/entenda-os-planos/'
        : 'https://phelcom.com/products/eyercloud/understanding-the-paid-plans/';

      return url
    },

    toDeatilsPaymentHistory() {
      this.$router.push({ name: 'clinic-show', params: { clinicID: this.session.currentClinic.id }, query: { tab: 'paymentHistory' } })
    },

    formatYearlyPriceDivided(item) {
      const selectedPrice = item.price;

      if (!selectedPrice?.unit_amount || selectedPrice.recurring?.interval !== 'year')
        return this.formatPriceByMonth(item);

      const monthlyValue = selectedPrice.unit_amount / 12;
      return this.formatCurrency(monthlyValue, selectedPrice.currency);
    },

    switchToYearly() {
      this.billingCycle = 'year';
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    async syncSubscriptionData() {
      const country = this.session?.currentClinic?.country;

      if (!country)
        return

      await this.$helpers.requestHandling(async () => {
        const updatedData = await this.subscriptionStore.updateUsage(country);

        if (updatedData) {
          this.subscriptionName = updatedData.subscriptionName;
          this.displayName = updatedData.displayName;
          this.usagePercentage = updatedData.percentage;
          this.planLimit = updatedData.planLimit;
          this.imageUsed = updatedData.imageTotal;
          this.subscription = updatedData.fullSubscription;

          if (this.subscription) {
            this.subscriptionId = this.subscription.id;
            this.updateBillingCycle(this.subscription);
            this.updateSubscriptionDaysRemaining(this.subscription);
          } else {
            this.subscriptionId = null;
            this.billingCycle = 'year';
          }

          this.fetchClinicData()
        }
      }, this);
    },

    // isPlanEnabled(item) {
    //   const planLimit = parseInt(item.metadata?.captures_limit || 0);
    //
    //   if (item.name === 'Corporate' || planLimit === 0)
    //     return false;
    //
    //   return this.imageUsed < planLimit;
    // },

    isPlanEnabled(item) {
      const planLimit = parseInt(item.metadata?.captures_limit || 0);
      const totalLimitWithBonus = planLimit + 10000;

      if (item.name === 'Corporate' || planLimit === 0)
        return false;

      return this.imageUsed < totalLimitWithBonus;
    },

    isCurrencyMismatch(item) {
      const currentCurrency = this.subscription?.items?.data?.[0]?.price?.currency?.toLowerCase();
      const newCurrency = item.price?.currency?.toLowerCase();
      return currentCurrency && newCurrency && currentCurrency !== newCurrency;
    },

    async fetchClinicData() {
      await this.$helpers.requestHandling(async () => {
        let params = {
          id: this.session.currentClinic.id
        }

        let response = await this.$clinicService.getClinic(params);
        this.clinic = response.clinic
      }, this);
    },

    async openPlansClinicDataOffcanvas() {
      const ok = await this.$refs.plansClinicDataOffcanvas.show({
        clinicID: this.clinic.id,
        clinic: this.clinic
      });

      if (ok) {
        await this.syncSubscriptionData();
      }
    },

    // Not-migrated: no legacy un-cancel, route through the normal migration/checkout flow instead.
    async reactivateSubscription(item) {
      if (!this.isMigrated) {
        await this.handlePlanAction(item);
        return;
      }

      await this.$helpers.requestHandling(async () => {
        const currentPriceID = this.subscription?.items?.data?.[0]?.price?.id;

        if (!currentPriceID) {
          this.$message.popup(this, 'unexpectedError', 'operationFail', 'error');
          return;
        }

        const params = {
          subscriptionId: this.subscriptionId,
          newPriceID: currentPriceID,
          cancelAtPeriodEnd: false
        };

        await this.$stripeService.updateSubscription(params);

        this.$message.popup(this, 'plans.reactivatedSuccess', 'success', 'success');

        await new Promise(resolve => setTimeout(resolve, 1000));
        await this.refreshSession();
      }, this);
    },

    async declineMigration() {
      const wantsToDecline = await this.$refs.plansMigrationNoticeOffcanvas.show({
        title: this.$i18n.t('plans.resubscription.declineTitle'),
        text: this.$i18n.t('plans.resubscription.declineConfirm'),
        okButton: this.$i18n.t('plans.resubscription.declineOkButton'),
        okButtonClass: 'btn-success',
      });
      if (!wantsToDecline)
        return;

      await this.$helpers.requestHandling(async () => {
        await this.$stripeService.declineMigration();

        this.$message.popup(this, 'plans.resubscription.declineSuccess', 'success', 'success');

        await this.syncSubscriptionData();
      }, this);
    },

    async handlePlanAction(item) {
      // Migrated (new account): any subscription that hasn't reached its period end yet
      // gets changed in place, regardless of cancellation state.
      if (this.isMigrated) {
        const now = Date.now() / 1000;
        const hasActiveSubscription = !!this.subscription && this.subscription.current_period_end > now;
        return hasActiveSubscription
          ? this.openPlansUpdateOffcanvas(item)
          : this.openPlansCheckoutOffcanvas(item);
      }

      // Not-migrated clinics have no real subscription on the new Stripe
      // account to update - they always need a fresh checkout, regardless of
      // whether the legacy plan data looks like an active subscription. Since
      // this only runs while a legacy plan is still active (the current-plan
      // card has no click handler), warn about the payment-system migration
      // before letting them proceed.
      const wantsToContinue = await this.$refs.plansMigrationNoticeOffcanvas.show({
        title: this.$i18n.t('plans.migrationChangeNotice.title'),
        text: this.$i18n.t('plans.migrationChangeNotice.text', { contactUrl: this.postSalesContactUrl('change') }),
        okButton: this.$i18n.t('plans.migrationChangeNotice.continueButton'),
        okButtonClass: 'btn-success',
        cancelButton: this.$i18n.t('plans.migrationChangeNotice.waitButton'),
        cancelButtonClass: 'btn-outline-secondary',
      });
      if (wantsToContinue)
        await this.openPlansCheckoutOffcanvas(item);
    },

    // No plan at all (Starter, or a legacy plan that already expired). Only
    // warn if there's real unused legacy credit to lose.
    async handleFreshCheckout(item) {
      if (!this.isMigrated && this.hasLegacyCredit) {
        const wantsToContinue = await this.$refs.plansMigrationNoticeOffcanvas.show({
          title: this.$i18n.t('plans.migrationResubscribeNotice.title'),
          text: this.$i18n.t('plans.migrationResubscribeNotice.text', { contactUrl: this.postSalesContactUrl('resubscribe') }),
          okButton: this.$i18n.t('plans.migrationResubscribeNotice.continueButton'),
          okButtonClass: 'btn-success',
          cancelButton: this.$i18n.t('plans.migrationResubscribeNotice.waitButton'),
          cancelButtonClass: 'btn-outline-secondary',
        });
        if (!wantsToContinue) return;
      }

      await this.openPlansCheckoutOffcanvas(item);
    }
  }
}
